// ============================================================================
// StatCard (GC-E) — tuile KPI re-skinnee du StatCard PoC (graphiques + reel)
// ============================================================================
// Le StatCard shadcn du PoC (props accent/tone/hint/variances) n'a pas d'equivalent
// direct dans @gs/ui (KpiCard = label/value/icon/variant, sans variances). Cette
// tuile locale mirroite l'AggCard de CadresOverview (GC-D) en generalisant les
// tones, pour les 5 KPI du tab Graphiques ET de la page Masse reelle. Partagee
// (source unique) au lieu de dupliquer la mecanique dans deux ecrans.
//
// Regle #28 : tokens dark-aware, zero hex. Regle #4 : zero emoji.

import type { ReactNode } from 'react';
import { GS_COMPONENTS } from '@gs/ui';

/** Teinte de la valeur principale. `accent`/`primary` = brand (mise en avant). */
export type StatTone = 'primary' | 'success' | 'neutral';

/** Sous-ligne de variance : rouge (defavorable) / emeraude (favorable) / neutre. */
export interface StatVariance {
  label: string;
  value: ReactNode;
  tone: 'negative' | 'positive' | 'neutral';
}

function valueToneClass(tone: StatTone): string {
  if (tone === 'primary') return 'text-brand-600 dark:text-brand-400';
  if (tone === 'success') return 'text-emerald-700 dark:text-emerald-400';
  return 'text-slate-800 dark:text-slate-100';
}

function varianceToneClass(tone: StatVariance['tone']): string {
  if (tone === 'negative') return 'text-red-600 dark:text-red-400';
  if (tone === 'positive') return 'text-emerald-600 dark:text-emerald-400';
  return 'text-slate-500 dark:text-slate-400';
}

export interface StatCardProps {
  label: string;
  value: string;
  hint?: string;
  tone?: StatTone;
  /** Liseret brand a gauche (StatCard `accent` PoC) — carte mise en avant. */
  accent?: boolean;
  variances?: StatVariance[];
}

export function StatCard({ label, value, hint, tone = 'neutral', accent, variances }: StatCardProps) {
  return (
    <div
      className={`${GS_COMPONENTS.card} flex flex-col gap-1 p-3${
        accent ? ' border-l-4 border-l-brand-500' : ''
      }`}
    >
      <p className="text-chip font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`text-head font-bold tabular-nums ${valueToneClass(tone)}`}>{value}</p>
      {hint ? <p className="text-meta text-slate-500 dark:text-slate-400">{hint}</p> : null}
      {variances && variances.length > 0 ? (
        <div className="mt-1 flex flex-col gap-0.5">
          {variances.map((v, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-meta">
              <span className="text-slate-500 dark:text-slate-400">{v.label}</span>
              <span className={`font-medium tabular-nums ${varianceToneClass(v.tone)}`}>{v.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
